import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Star, ExternalLink, Check, X } from 'lucide-react';
import axios from 'axios';
import { SEO } from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RETURNS = {
  fashionphile: '30 days',
  therealreal: '14 days',
  '1stdibs': 'Varies by seller',
  vestiaire: '14 days',
};

const StarRating = ({ rating }) => (
  <span className="inline-flex items-center gap-1.5 text-sm">
    <Star className="w-3.5 h-3.5 text-[#C69C6D] fill-[#C69C6D]" strokeWidth={1} />
    <span className="font-heading text-[#111]">{rating}</span>
  </span>
);

export const ComparePage = () => {
  const [partners, setPartners] = useState([]);
  const [comparisons, setComparisons] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [partnersRes, comparisonsRes] = await Promise.all([
          axios.get(`${API}/partners`),
          axios.get(`${API}/comparisons`),
        ]);
        setPartners(partnersRes.data);
        setComparisons(comparisonsRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    window.scrollTo(0, 0);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF9F6]">
        <div className="w-6 h-6 border border-[#111] border-t-transparent animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="compare-page" className="bg-[#FAF9F6]">
      <SEO
        title="Compare Pre-Owned Jewelry Platforms"
        description="Side-by-side comparison of Fashionphile vs The RealReal vs 1stDibs vs Vestiaire Collective. Authentication, return policies, prices, and editor scores."
        url="/compare"
      />

      {/* Editorial Hero */}
      <section className="border-b border-[#E5E0D8]">
        <div className="container-editorial py-20 lg:py-28">
          <nav className="flex items-center gap-3 text-xs tracking-[0.15em] uppercase text-[#888] mb-12">
            <Link to="/" className="hover:text-[#111] transition-colors">Home</Link>
            <span>/</span>
            <span className="text-[#111]">Compare Platforms</span>
          </nav>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
            <div className="lg:col-span-8">
              <p className="overline overline-gold mb-6 animate-fade-in">Side-by-Side</p>
              <h1 className="heading-editorial heading-editorial-lg text-[#111] animate-fade-in-up">
                Compare The <br />
                <span className="italic">Marketplaces</span>
              </h1>
            </div>
            <div className="lg:col-span-4 lg:border-l border-[#E5E0D8] lg:pl-8">
              <p className="text-[#555] leading-relaxed">
                Authentication processes, pricing, selection, and buyer protection across every
                top resale marketplace — vetted by our GIA-certified team.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison "Table" — semantic grid, not HTML table (avoids visual-editor wrapper hydration issue) */}
      <section className="section-editorial border-b border-[#E5E0D8]">
        <div className="container-editorial">
          <div className="flex items-end justify-between mb-8 pb-6 border-b border-[#E5E0D8]">
            <div>
              <p className="overline overline-gold mb-3">At-A-Glance</p>
              <h2 className="heading-editorial text-3xl lg:text-4xl">Platform Overview</h2>
            </div>
          </div>

          <div className="bg-[#FAF9F6] border border-[#E5E0D8] overflow-x-auto" data-testid="comparison-table">
            <div className="min-w-[860px]">
              {/* Header row */}
              <div className="grid grid-cols-[1.4fr_0.7fr_1.2fr_0.9fr_0.8fr_0.8fr] bg-[#111] text-white text-[10px] tracking-[0.2em] uppercase">
                <div className="px-6 py-5 font-medium">Platform</div>
                <div className="px-6 py-5 font-medium">Rating</div>
                <div className="px-6 py-5 font-medium">Specialty</div>
                <div className="px-6 py-5 font-medium">Authentication</div>
                <div className="px-6 py-5 font-medium">Returns</div>
                <div className="px-6 py-5 font-medium">Action</div>
              </div>
              {/* Data rows */}
              {partners.map((p, i) => (
                <div
                  key={p.id}
                  className={`grid grid-cols-[1.4fr_0.7fr_1.2fr_0.9fr_0.8fr_0.8fr] items-center hover:bg-white transition-colors ${
                    i < partners.length - 1 ? 'border-b border-[#E5E0D8]' : ''
                  }`}
                  data-testid={`compare-row-${p.slug}`}
                >
                  <div className="px-6 py-5">
                    <p className="font-heading text-lg text-[#111]">{p.name}</p>
                  </div>
                  <div className="px-6 py-5">
                    <StarRating rating={p.rating} />
                  </div>
                  <div className="px-6 py-5 text-sm text-[#555]">{p.specialty || '—'}</div>
                  <div className="px-6 py-5">
                    <Check className="w-4 h-4 text-[#C69C6D]" strokeWidth={1.5} />
                  </div>
                  <div className="px-6 py-5 text-sm text-[#555]">{RETURNS[p.slug] || '14 days'}</div>
                  <div className="px-6 py-5">
                    <a
                      href={p.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs tracking-[0.15em] uppercase text-[#111] hover:text-[#C69C6D] inline-flex items-center gap-1.5 border-b border-[#111] hover:border-[#C69C6D] pb-0.5 transition-colors"
                    >
                      Visit
                      <ExternalLink className="w-3 h-3" strokeWidth={1.5} />
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Detailed Editorial Reviews */}
      <section className="section-editorial">
        <div className="container-editorial">
          <div className="flex items-end justify-between mb-12 pb-6 border-b border-[#E5E0D8]">
            <div>
              <p className="overline overline-gold mb-3">Long-Form</p>
              <h2 className="heading-editorial text-4xl lg:text-5xl">Editorial Reviews</h2>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {partners.map((partner) => (
              <article
                key={partner.id}
                className="bg-[#FAF9F6] border border-[#E5E0D8] hover:border-[#111] transition-colors p-8 lg:p-10 flex flex-col"
                data-testid={`partner-card-${partner.slug}`}
              >
                <header className="flex items-start justify-between mb-6 pb-6 border-b border-[#E5E0D8]">
                  <div>
                    <h3 className="font-heading text-2xl text-[#111] mb-1">{partner.name}</h3>
                    <p className="overline">{partner.specialty || 'Marketplace'}</p>
                  </div>
                  <StarRating rating={partner.rating} />
                </header>

                <p className="text-[#555] leading-relaxed mb-8 flex-1">{partner.description}</p>

                <div className="grid grid-cols-2 gap-6 mb-8">
                  <div>
                    <p className="overline mb-3 flex items-center gap-2">
                      <Check className="w-3 h-3 text-[#C69C6D]" strokeWidth={1.5} /> Pros
                    </p>
                    <ul className="space-y-2">
                      {(partner.pros || []).map((pro, i) => (
                        <li key={i} className="text-sm text-[#555] leading-relaxed flex items-start gap-2">
                          <span className="w-1 h-1 bg-[#C69C6D] rounded-full mt-2 flex-shrink-0" />
                          {pro}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <p className="overline mb-3 flex items-center gap-2">
                      <X className="w-3 h-3 text-[#888]" strokeWidth={1.5} /> Cons
                    </p>
                    <ul className="space-y-2">
                      {(partner.cons || []).map((con, i) => (
                        <li key={i} className="text-sm text-[#555] leading-relaxed flex items-start gap-2">
                          <span className="w-1 h-1 bg-[#888] rounded-full mt-2 flex-shrink-0" />
                          {con}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="flex flex-wrap gap-3 pt-6 border-t border-[#E5E0D8]">
                  <a
                    href={partner.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-editorial-inverse btn-editorial inline-flex items-center justify-center gap-2 flex-1"
                    data-testid={`visit-${partner.slug}-btn`}
                  >
                    Visit {partner.name}
                    <ExternalLink className="w-3 h-3" strokeWidth={1.5} />
                  </a>
                  <Link
                    to={`/partners/${partner.slug}`}
                    className="btn-editorial inline-flex items-center justify-center gap-2"
                  >
                    Full Review
                  </Link>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* In-Depth Comparisons */}
      {comparisons.length > 0 && (
        <section className="section-editorial border-t border-[#E5E0D8] bg-white/40">
          <div className="container-editorial">
            <div className="flex items-end justify-between mb-12 pb-6 border-b border-[#E5E0D8]">
              <div>
                <p className="overline overline-gold mb-3">Head-to-Head</p>
                <h2 className="heading-editorial text-4xl lg:text-5xl">In-Depth Comparisons</h2>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {comparisons.map((c) => (
                <Link
                  key={c.id}
                  to={`/compare/${c.slug}`}
                  className="group block border border-[#E5E0D8] bg-[#FAF9F6] p-8 lg:p-10 hover:border-[#111] transition-colors"
                  data-testid={`comparison-article-${c.slug}`}
                >
                  <p className="overline overline-gold mb-4">Comparison</p>
                  <h3 className="font-heading text-2xl lg:text-3xl text-[#111] mb-4 group-hover:text-[#C69C6D] transition-colors">
                    {c.title}
                  </h3>
                  <p className="text-[#555] leading-relaxed mb-6 line-clamp-3">{c.excerpt}</p>
                  <span className="inline-flex items-center gap-2 text-xs tracking-[0.15em] uppercase text-[#111] group-hover:text-[#C69C6D] border-b border-[#111] group-hover:border-[#C69C6D] pb-1 transition-colors">
                    Read Comparison
                    <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" strokeWidth={1.5} />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="bg-[#111] text-white py-20 lg:py-28">
        <div className="container-editorial text-center">
          <p className="overline text-[#C69C6D] mb-6">Ready When You Are</p>
          <h2 className="heading-editorial text-3xl lg:text-5xl mb-6">
            Start Shopping <span className="italic">Pre-Owned</span>
          </h2>
          <p className="text-white/60 text-lg leading-relaxed mb-10 max-w-2xl mx-auto">
            Choose a platform from our trusted partners and discover authenticated pre-owned
            luxury jewelry today.
          </p>
          <a
            href="https://www.fashionphile.com/jewelry"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-editorial border-white text-white hover:bg-white hover:text-[#111] inline-flex items-center gap-2"
            data-testid="cta-shop-btn"
          >
            Shop on Fashionphile
            <ExternalLink className="w-3 h-3" strokeWidth={1.5} />
          </a>
        </div>
      </section>
    </div>
  );
};

export default ComparePage;
